/**
 * @author Vibhor <vibhor.sharma@314ecorp.com>
 * @description PDF Viewer component using PDF.js
 */
/* eslint-disable react-hooks/refs */
/* eslint-disable react-hooks/use-memo */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import _ from 'lodash';
import clsx from 'clsx';
import log from 'loglevel';
import { Button, Flex, Input, Result, Skeleton, Tooltip, Typography } from 'antd';
import { Up, Down, ZoomIn, ZoomOut } from '@icon-park/react';

import usePdf from 'hooks/pdfjs/usePdf';

interface IProps {
	file: string;
	width?: React.CSSProperties['width'];
	height?: React.CSSProperties['height'];
	showThumbnails?: boolean;
	fileName?: string;
}

const PDFViewer: React.FC<IProps> = ({ file, width = '80vw', height = '80vh', showThumbnails = true, fileName }) => {
	const canvasRefs = useRef<(HTMLCanvasElement | null)[]>([]);
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const [thumbnails, setThumbnails] = useState<string[]>([]);
	const [thumbnailLoading, setThumbnailLoading] = useState(false);
	const [pageInput, setPageInput] = useState('');
	const [currentVisiblePage, setCurrentVisiblePage] = useState(0);
	const [displayPage, setDisplayPage] = useState(0);

	const { totalPages, zoom, loading, error, loadDocument, renderPage, renderThumbnail, zoomIn, zoomOut } = usePdf();

	useEffect(() => {
		setPageInput((displayPage + 1).toString());
	}, [displayPage]);

	// Load document
	useEffect(() => {
		const init = async () => {
			try {
				canvasRefs.current = [];
				const response = await fetch(file);
				const arrayBuffer = await response.arrayBuffer();
				await loadDocument(arrayBuffer);
			} catch (err) {
				log.error('Failed to load PDF:', err);
			}
		};
		init();
	}, [file, loadDocument]);

	// Render all pages
	useEffect(() => {
		const renderAllPages = async () => {
			if (loading || error || totalPages === 0) {
				return;
			}

			try {
				for (let i = 0; i < totalPages; i++) {
					const canvas = canvasRefs.current[i];
					if (canvas) {
						const imageBitmap = await renderPage(i, zoom);
						const ctx = canvas.getContext('2d');

						if (ctx) {
							canvas.width = imageBitmap.width;
							canvas.height = imageBitmap.height;
							ctx.drawImage(imageBitmap, 0, 0);
						}
					}
				}
			} catch (err) {
				log.error('Failed to render pages:', err);
			}
		};

		renderAllPages();
	}, [zoom, loading, error, totalPages, renderPage]);

	// Load thumbnails
	useEffect(() => {
		const loadThumbnails = async () => {
			if (!showThumbnails || totalPages === 0) {
				return;
			}

			setThumbnailLoading(true);
			try {
				const thumbnailPromises = _.map(_.range(totalPages), (i) => renderThumbnail(i));
				const thumbnailUrls = await Promise.all(thumbnailPromises);
				setThumbnails(thumbnailUrls);
			} catch (err) {
				log.error('Failed to load thumbnails:', err);
			} finally {
				setThumbnailLoading(false);
			}
		};

		loadThumbnails();
	}, [showThumbnails, totalPages, renderThumbnail]);

	// Throttled page detection for performance
	const detectCurrentPage = () => {
		if (!scrollContainerRef.current) {
			return;
		}

		const container = scrollContainerRef.current;
		const containerRect = container.getBoundingClientRect();
		const containerCenter = containerRect.top + containerRect.height / 2;

		let closestPage = 0;
		let minDistance = Infinity;

		_.forEach(canvasRefs.current, (canvas, index) => {
			if (canvas) {
				const canvasRect = canvas.getBoundingClientRect();
				const canvasCenter = canvasRect.top + canvasRect.height / 2;
				const distance = Math.abs(containerCenter - canvasCenter);

				if (distance < minDistance) {
					minDistance = distance;
					closestPage = index;
				}
			}
		});

		if (closestPage !== currentVisiblePage) {
			setCurrentVisiblePage(closestPage);
		}
	};

	// Debounced scroll handler for performance
	const debouncedScrollHandler = useCallback(
		_.debounce(() => {
			detectCurrentPage();
		}, 100),
		[detectCurrentPage],
	);

	// Throttled display update to prevent excessive UI updates
	const throttledDisplayUpdate = useCallback(
		_.throttle((page: number) => {
			setDisplayPage(page);
		}, 200),
		[],
	);

	// Update display page when current visible page changes
	useEffect(() => {
		throttledDisplayUpdate(currentVisiblePage);
	}, [currentVisiblePage, throttledDisplayUpdate]);

	// Scroll to specific page
	const scrollToPage = (pageIndex: number) => {
		const canvas = canvasRefs.current[pageIndex];
		if (canvas && scrollContainerRef.current) {
			canvas.scrollIntoView({ behavior: 'auto', block: 'start' });
		}
	};

	// Handle page input
	const handlePageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setPageInput(e.target.value);
	};

	const handlePageInputKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			const pageNumber = parseInt(pageInput, 10) - 1; // Convert to 0-based index
			if (pageNumber >= 0 && pageNumber < totalPages) {
				scrollToPage(pageNumber);
				setPageInput('');
				setDisplayPage(pageNumber);
			}
		}
	};

	// Scroll to current page in thumbnails (throttled)
	const scrollThumbnailToCurrent = useCallback(
		_.throttle(() => {
			if (showThumbnails && thumbnails.length > 0) {
				const thumbnailElement = document.getElementById(`thumbnail-${displayPage}`);
				if (thumbnailElement) {
					thumbnailElement.scrollIntoView({
						behavior: 'auto',
						block: 'nearest',
					});
				}
			}
		}, 300),
		[showThumbnails, thumbnails.length, displayPage],
	);

	// Update thumbnail scroll when display page changes
	useEffect(() => {
		scrollThumbnailToCurrent();
	}, [displayPage, scrollThumbnailToCurrent]);

	// Cleanup thumbnails on unmount
	useEffect(() => {
		return () => {
			_.forEach(thumbnails, (url) => URL.revokeObjectURL(url));
		};
	}, [thumbnails]);

	if (error) {
		return <Result status='error' title='Please try again later' subTitle={error} />;
	}

	return (
		<Flex style={{ width, height }}>
			{/* Thumbnails Sidebar */}
			{showThumbnails && (
				<div className='border-neutral-4 h-full w-30 overflow-y-auto border-r p-2.5'>
					{thumbnailLoading ? (
						<Skeleton active />
					) : (
						_.map(thumbnails, (thumbnail, index) => (
							<div
								key={index}
								id={`thumbnail-${index}`}
								className={clsx('mb-2.5 cursor-pointer overflow-hidden rounded-md', {
									'border-2 border-[#007BFF]': displayPage === index,
									'border border-[#ddd]': displayPage !== index,
								})}
								onClick={() => scrollToPage(index)}
								role='button'
								tabIndex={0}
								onKeyDown={(e) => {
									if (e.key === 'Enter') {
										scrollToPage(index);
									}
								}}
							>
								<img src={thumbnail} alt={`Page ${index + 1}`} className='h-auto w-full' />
								<div
									className={clsx('p-1 text-center text-xs', {
										'bg-[#007bff] text-white': displayPage === index,
										'bg-[#f8f9fa] text-black': displayPage !== index,
									})}
								>
									{index + 1}
								</div>
							</div>
						))
					)}
				</div>
			)}

			{/* Main Viewer */}
			<Flex vertical className='flex-1'>
				{/* Controls */}
				<Flex
					align='center'
					justify='center'
					gap={24}
					className='sticky top-0 z-1000 border-b border-[#F0F0F0] bg-white px-1.25 py-2'
				>
					<Flex gap={8} align='center' className='absolute left-4'>
						<Typography.Title level={5} className='m-0'>
							{_.startCase(fileName)}
						</Typography.Title>
					</Flex>
					<Flex gap={16} align='center'>
						<Tooltip title='Previous Page'>
							<Button
								icon={<Up size={16} />}
								onClick={() => scrollToPage(Math.max(0, displayPage - 1))}
								disabled={displayPage === 0}
								type='text'
								shape='circle'
							/>
						</Tooltip>

						<Tooltip title='Next Page'>
							<Button
								icon={<Down size={16} />}
								onClick={() => scrollToPage(Math.min(totalPages - 1, displayPage + 1))}
								disabled={displayPage === totalPages - 1}
								type='text'
								shape='circle'
							/>
						</Tooltip>

						{/* Page Input */}
						<Flex gap={8} align='center'>
							<Input
								type='number'
								min='1'
								max={totalPages}
								value={pageInput}
								onBlur={() => {
									setPageInput((displayPage + 1).toString());
								}}
								onChange={handlePageInputChange}
								onKeyPress={handlePageInputKeyPress}
								placeholder={'Go to'}
								className='w-12.5 px-1.25 py-0.5 text-xs'
							/>
							{`/ ${totalPages}`}
						</Flex>

						<Tooltip title='Zoom Out'>
							<Button icon={<ZoomOut size={16} />} onClick={zoomOut} type='text' shape='circle' disabled={zoom <= 0.5} />
						</Tooltip>

						<Tooltip title='Zoom In'>
							<Button icon={<ZoomIn size={16} />} onClick={zoomIn} type='text' shape='circle' disabled={zoom >= 2.5} />
						</Tooltip>
					</Flex>
				</Flex>

				{/* Continuous Scroll Container */}
				<div ref={scrollContainerRef} onScroll={debouncedScrollHandler} className='bg-neutral-1 flex-1 overflow-auto'>
					{loading ? (
						<Skeleton active />
					) : (
						/* Render all pages in continuous layout */
						_.map(_.range(totalPages), (index) => (
							<Flex
								key={index}
								justify='center'
								className={clsx('bg-neutral-1 p-4', { 'border-b border-[#ddd]': index < totalPages - 1 })}
							>
								<canvas
									ref={(el) => {
										canvasRefs.current[index] = el;
									}}
									className='border-neutral-4 h-auto max-w-full border bg-white'
								/>
							</Flex>
						))
					)}
				</div>
			</Flex>
		</Flex>
	);
};

export default PDFViewer;
